import { sb } from '../supabase';
import { clarityBlocked } from './clarity';

// Growth OS — Microsoft Clarity as stored by the daily clarity-export pull
// (growth_clarity_pulls / growth_clarity_metrics). Rows are Clarity's raw
// output; Traffic's documented fields are read with both spellings Clarity
// might use, since its own docs spell one "distantUserCount".

export const CLARITY_PROJECT = process.env.REACT_APP_CLARITY_ID || '';
export const clarityUrl = (page = 'dashboard') => (CLARITY_PROJECT ? `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT}/${page}` : null);

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const numOrNull = (v) => (v == null || v === '' ? null : num(v));
// A page as a person reads it: no domain, Arabic decoded.
export function prettyUrl(u) {
  const path = String(u || '').replace(/^https?:\/\/[^/]+/, '') || '/';
  try { return decodeURIComponent(path); } catch { return path; }
}
export const trafficOf = (row = {}) => ({
  sessions: num(row.totalSessionCount),
  bots: num(row.totalBotSessionCount),
  users: numOrNull(row.distinctUserCount ?? row.distantUserCount),   // absent ≠ zero
  pagesPerSession: Number(row.PagesPerSessionPercentage ?? row.pagesPerSessionPercentage) || null,
});

// A dimension's value in a row. The name we ASK with is not the name Clarity
// ANSWERS with: we send dimension1=URL and the rows come back keyed "Url"
// (seen in the first live pull, 2026-11-01), so match without case.
export function dimValue(row = {}, dim) {
  if (row[dim] !== undefined) return row[dim];
  const k = Object.keys(row).find((x) => x.toLowerCase() === String(dim).toLowerCase());
  return k === undefined ? undefined : row[k];
}

// The latest pull and its rows, grouped as the page shows them. Team pages
// (/admin…, /agent) are left out: Clarity no longer records them, and any
// that an older pull holds are not trader behaviour.
export function summarizeClarity(pull, rows) {
  const traffic = rows.filter((r) => r.metric_name === 'Traffic')
    .filter((r) => !(r.dimensions.includes('URL') && clarityBlocked(prettyUrl(dimValue(r.row, 'URL')))));
  const by = (dim) => traffic.filter((r) => r.dimensions.join('+') === dim)
    .map((r) => ({
      key: r.dimensions.map((d) => (d === 'URL' ? prettyUrl(dimValue(r.row, d)) : dimValue(r.row, d))).filter((v) => v != null && String(v).trim() !== '').join(' · ') || '—',
      ...trafficOf(r.row),
    }))
    .sort((a, b) => b.sessions - a.sessions);
  const device = by('Device+OS');
  const total = device.reduce((t, r) => ({ sessions: t.sessions + r.sessions, users: t.users + (r.users || 0) }), { sessions: 0, users: 0 });
  const others = {};
  rows.filter((r) => r.metric_name !== 'Traffic').forEach((r) => { (others[r.metric_name] = others[r.metric_name] || []).push(r); });
  return { pull, total, device, pages: by('URL').slice(0, 10), sources: by('Source+Medium+Campaign').slice(0, 10), channels: by('Channel'), others };
}

export async function fetchClarity() {
  const { data: pulls, error } = await sb.from('growth_clarity_pulls').select('*').order('started_at', { ascending: false }).limit(5);
  if (error) throw error;
  const lastGood = (pulls || []).find((p) => p.status === 'ok' || p.status === 'partial');
  if (!lastGood) return { pulls: pulls || [], latest: null };
  const { data: rows, error: rErr } = await sb.from('growth_clarity_metrics').select('metric_name, dimensions, row').eq('pull_id', lastGood.id).limit(5000);
  if (rErr) throw rErr;
  return { pulls: pulls || [], latest: summarizeClarity(lastGood, rows || []) };
}

// For «حالة التكاملات»: the script, and the last pull.
export function clarityStatus(pulls, project = CLARITY_PROJECT) {
  if (!project) return { state: 'neutral', label: 'غير مفعّل' };
  const last = (pulls || [])[0];
  if (!last) return { state: 'neutral', label: 'يسجّل · بانتظار أول سحب' };
  if (last.status === 'error') return { state: 'bad', label: 'فشل آخر سحب' };
  const fresh = Date.now() - Date.parse(last.finished_at || last.started_at) < 36 * 3600e3;
  return fresh ? { state: 'ok', label: 'يسجّل ويُسحب يومياً' } : { state: 'bad', label: 'السحب متأخر' };
}

// ── By day, for a range (owner, 2026-11-01) ──────────────────────────────────
// Each pull covers the 24 hours before it ran, so a pull belongs to the Riyadh
// day 12 hours before it started (the 03:00 run on 2 Nov is 1 Nov; a manual
// run at 22:27 on 1 Nov is also 1 Nov). Where two pulls cover the same day the
// later one wins — the days never overlap in a total.
const DAY_MS = 864e5;
const riyadhYmd = (ms) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh' }).format(new Date(ms));
export const coveredDay = (pull) => riyadhYmd(Date.parse(pull.started_at) - 12 * 3600e3);
// Sessions of the team were recorded until the console was taken out of
// Clarity on 1 Nov 2026; days up to then include them.
export const TEAM_IN_CLARITY_UNTIL = '2026-11-01';
// The daily pull began on this day; earlier days are not "missing".
export const CLARITY_FIRST_DAY = '2026-11-01';

export function pickDays(pulls, from, to) {
  const best = {};
  (pulls || []).filter((p) => p.status === 'ok' || p.status === 'partial').forEach((p) => {
    const d = coveredDay(p);
    if (d < from || d > to) return;
    if (!best[d] || p.started_at > best[d].started_at) best[d] = p;
  });
  return Object.keys(best).sort().map((day) => ({ day, pull: best[day] }));
}

const pathOf = (r) => prettyUrl(dimValue(r.row, 'URL'));
const keyOf = (r) => r.dimensions.map((d) => (d === 'URL' ? prettyUrl(dimValue(r.row, d)) : dimValue(r.row, d)))
  .filter((v) => v != null && String(v).trim() !== '').join(' · ') || '—';

function addTraffic(acc, row) {
  const t = trafficOf(row);
  acc.sessions += t.sessions; acc.bots += t.bots;
  if (t.users != null) acc.users = (acc.users || 0) + t.users;
  if (t.pagesPerSession != null) { acc._pps += t.pagesPerSession * t.sessions; acc._ppsW += t.sessions; }
  return acc;
}
const finishTraffic = (a) => ({ key: a.key, parts: a.parts, sessions: a.sessions, bots: a.bots, users: a.users,
  pagesPerSession: a._ppsW ? a._pps / a._ppsW : null });

// Count metrics: subTotal adds up; "sessions with it" is sessionsCount × the
// percentage. Scroll depth and time are per-session averages, weighted by sessions.
function metricAcc() { return { total: 0, withIt: 0, sessions: 0, _sd: 0, _sdW: 0, _act: 0, _tot: 0, _tW: 0, seen: false }; }
function addMetric(acc, metric, row, sessionsHint) {
  acc.seen = true;
  if (metric === 'ScrollDepth') {
    const v = Number(row.averageScrollDepth); const w = sessionsHint || 1;
    if (Number.isFinite(v)) { acc._sd += v * w; acc._sdW += w; }
  } else if (metric === 'EngagementTime') {
    const w = sessionsHint || 1;
    acc._act += (Number(row.activeTime) || 0) * w; acc._tot += (Number(row.totalTime) || 0) * w; acc._tW += w;
  } else {
    const s = Number(row.sessionsCount) || 0;
    acc.total += Number(row.subTotal) || 0;
    acc.sessions += s;
    acc.withIt += ((Number(row.sessionsWithMetricPercentage) || 0) * s) / 100;
  }
}
function finishMetric(acc) {
  return {
    seen: acc.seen, total: acc.total, sessions: acc.sessions, withIt: Math.round(acc.withIt),
    pct: acc.sessions ? (acc.withIt / acc.sessions) * 100 : null,
    scroll: acc._sdW ? acc._sd / acc._sdW : null,
    active: acc._tW ? acc._act / acc._tW : null, totalTime: acc._tW ? acc._tot / acc._tW : null,
  };
}

// days: [{ day, pull }]; rows: growth_clarity_metrics rows of those pulls.
export function summarizeClarityDays(days, rows) {
  const ids = new Set(days.map((d) => d.pull.id));
  const mine = (rows || []).filter((r) => ids.has(r.pull_id));
  const isPage = (r) => r.dimensions.length === 1 && r.dimensions[0] === 'URL';
  const keep = (r) => !(isPage(r) && clarityBlocked(pathOf(r)));

  // Traffic per dimension set, summed over days.
  const traffic = {};
  const sessionsAt = {};   // pull|dims|key → sessions, to weight the averages
  mine.filter((r) => r.metric_name === 'Traffic' && keep(r)).forEach((r) => {
    const dim = r.dimensions.join('+'); const key = keyOf(r);
    traffic[dim] = traffic[dim] || {};
    const parts = Object.fromEntries(r.dimensions.map((d) => [d, d === 'URL' ? pathOf(r) : dimValue(r.row, d)]));
    traffic[dim][key] = addTraffic(traffic[dim][key] || { key, parts, sessions: 0, bots: 0, users: null, _pps: 0, _ppsW: 0 }, r.row);
    sessionsAt[`${r.pull_id}|${dim}|${key}`] = trafficOf(r.row).sessions;
  });
  // Rows with no human session (bots only) are noise in a list people read.
  const list = (dim, n = 50) => Object.values(traffic[dim] || {}).map(finishTraffic).filter((r) => r.sessions > 0).sort((a, b) => b.sessions - a.sessions).slice(0, n);

  // Site-wide metric cards: from the Channel rows (every session has exactly one channel).
  const site = {};
  CLARITY_METRIC_KEYS.forEach((m) => { site[m] = metricAcc(); });
  mine.filter((r) => r.dimensions.join('+') === 'Channel' && site[r.metric_name]).forEach((r) => {
    addMetric(site[r.metric_name], r.metric_name, r.row, sessionsAt[`${r.pull_id}|Channel|${keyOf(r)}`]);
  });

  // Per page.
  const pages = {};
  mine.filter((r) => isPage(r) && keep(r)).forEach((r) => {
    const path = pathOf(r);
    const p = pages[path] = pages[path] || { path, traffic: { key: path, sessions: 0, bots: 0, users: null, _pps: 0, _ppsW: 0 }, m: {} };
    if (r.metric_name === 'Traffic') { addTraffic(p.traffic, r.row); return; }
    if (!CLARITY_METRIC_KEYS.includes(r.metric_name)) return;
    p.m[r.metric_name] = p.m[r.metric_name] || metricAcc();
    addMetric(p.m[r.metric_name], r.metric_name, r.row, sessionsAt[`${r.pull_id}|URL|${path}`]);
  });
  const pageList = Object.values(pages).map((p) => ({
    path: p.path, ...finishTraffic(p.traffic),
    m: Object.fromEntries(Object.entries(p.m).map(([k, v]) => [k, finishMetric(v)])),
  })).filter((p) => p.sessions > 0).sort((a, b) => b.sessions - a.sessions);

  const device = list('Device+OS');
  return {
    days,
    total: device.reduce((t, r) => ({ sessions: t.sessions + r.sessions, users: t.users + (r.users || 0), bots: t.bots + r.bots }), { sessions: 0, users: 0, bots: 0 }),
    device, channels: list('Channel'), sources: list('Source+Medium+Campaign', 15), pages: pageList.slice(0, 25),
    metrics: Object.fromEntries(Object.entries(site).map(([k, v]) => [k, finishMetric(v)])),
    includesTeam: days.some((d) => d.day <= TEAM_IN_CLARITY_UNTIL),
  };
}
const CLARITY_METRIC_KEYS = ['RageClickCount', 'DeadClickCount', 'QuickbackClick', 'ExcessiveScroll', 'ScriptErrorCount', 'ScrollDepth', 'EngagementTime'];

// The pulls whose day falls in [from, to] (Riyadh dates), and their rows.
export async function fetchClarityRange(from, to) {
  const lo = new Date(Date.parse(`${from}T00:00:00+03:00`)).toISOString();
  const hi = new Date(Date.parse(`${to}T00:00:00+03:00`) + 2 * DAY_MS).toISOString();
  const { data: pulls, error } = await sb.from('growth_clarity_pulls').select('*')
    .gte('started_at', lo).lt('started_at', hi).order('started_at', { ascending: false }).limit(200);
  if (error) throw error;
  const days = pickDays(pulls, from, to);
  if (!days.length) return { pulls: pulls || [], summary: null };
  const { data: rows, error: rErr } = await sb.from('growth_clarity_metrics').select('pull_id, metric_name, dimensions, row')
    .in('pull_id', days.map((d) => d.pull.id)).limit(20000);
  if (rErr) throw rErr;
  return { pulls: pulls || [], summary: summarizeClarityDays(days, rows || []) };
}
